import type { AllMembershipPlanType, MarketedMembershipPlanType, MembershipPlanShape } from '@frontend/api-client';
import { useEffect, useFeatureIsOn, useFeatureValue, useMemo, useRef } from '@frontend/react';
import type { PricingState, ValidatedPromoInput } from '@frontend/react-domain';
import { useMembershipPlans as useMembershipPlansBase } from '@frontend/react-domain';
import { useRetrievePlanTiersQuery } from './api';

export { getValidatedPromoInput, type PricingState, type ValidatedPromoInput } from '@frontend/react-domain';

type PricingSource = 'baseFallback' | 'currentPromo' | 'previousDisplay';

type DisplayMembershipPlans = {
  activePromoCode?: string;
  displayPlanLookup: Partial<Record<AllMembershipPlanType, MembershipPlanShape>>;
  displaySelectablePlans: Array<MembershipPlanShape & { planType: MarketedMembershipPlanType }>;
  pricingError?: 'planTiersFailed' | 'promoPricingFailed';
  pricingSource: PricingSource;
  pricingState: PricingState;
};

type UseMembershipPlansOptions = {
  validatedPromo?: ValidatedPromoInput;
};

type SettledMembershipPlans = Pick<
  DisplayMembershipPlans,
  'activePromoCode' | 'displayPlanLookup' | 'displaySelectablePlans' | 'pricingSource'
>;

const createSettledMembershipPlans = ({
  activePromoCode,
  planLookup,
  pricingSource,
  selectablePlans,
}: {
  activePromoCode?: string;
  planLookup: Partial<Record<AllMembershipPlanType, MembershipPlanShape>>;
  pricingSource: SettledMembershipPlans['pricingSource'];
  selectablePlans: Array<MembershipPlanShape & { planType: MarketedMembershipPlanType }>;
}) =>
  ({
    activePromoCode,
    displayPlanLookup: planLookup,
    displaySelectablePlans: selectablePlans,
    pricingSource,
  }) satisfies SettledMembershipPlans;

const getSettledMembershipPlansCandidate = ({
  baseMembershipPlans,
  currentMembershipPlans,
  normalizedPromoCode,
}: {
  baseMembershipPlans: ReturnType<typeof useMembershipPlansBase>;
  currentMembershipPlans: ReturnType<typeof useMembershipPlansBase>;
  normalizedPromoCode?: string;
}): SettledMembershipPlans | undefined => {
  if (!normalizedPromoCode) {
    return baseMembershipPlans.hasCurrentPlanData && !baseMembershipPlans.isError
      ? createSettledMembershipPlans({
          planLookup: baseMembershipPlans.planLookup,
          pricingSource: 'baseFallback',
          selectablePlans: baseMembershipPlans.selectablePlans,
        })
      : undefined;
  }

  if (currentMembershipPlans.hasCurrentPlanData && !currentMembershipPlans.isError) {
    return createSettledMembershipPlans({
      activePromoCode: normalizedPromoCode,
      planLookup: currentMembershipPlans.planLookup,
      pricingSource: 'currentPromo',
      selectablePlans: currentMembershipPlans.selectablePlans,
    });
  }

  return currentMembershipPlans.isError && baseMembershipPlans.hasCurrentPlanData
    ? createSettledMembershipPlans({
        activePromoCode: normalizedPromoCode,
        planLookup: baseMembershipPlans.planLookup,
        pricingSource: 'baseFallback',
        selectablePlans: baseMembershipPlans.selectablePlans,
      })
    : undefined;
};

const resolveDisplayState = ({
  basePlans,
  currentPlans,
  lastSettled,
  promoCode,
}: {
  basePlans: ReturnType<typeof useMembershipPlansBase>;
  currentPlans: ReturnType<typeof useMembershipPlansBase>;
  lastSettled?: SettledMembershipPlans;
  promoCode?: string;
}): DisplayMembershipPlans => {
  if (!promoCode) {
    return {
      activePromoCode: undefined,
      displayPlanLookup: basePlans.hasCurrentPlanData ? basePlans.planLookup : {},
      displaySelectablePlans: basePlans.hasCurrentPlanData ? basePlans.selectablePlans : [],
      pricingError: basePlans.isError ? 'planTiersFailed' : undefined,
      pricingSource: 'baseFallback',
      pricingState: basePlans.hasCurrentPlanData ? 'ready' : basePlans.isError ? 'error' : 'initialLoading',
    };
  }

  if (currentPlans.hasCurrentPlanData) {
    return {
      activePromoCode: promoCode,
      displayPlanLookup: currentPlans.planLookup,
      displaySelectablePlans: currentPlans.selectablePlans,
      pricingError: undefined,
      pricingSource: 'currentPromo',
      pricingState: 'ready',
    };
  }

  if (currentPlans.isError && basePlans.hasCurrentPlanData) {
    return {
      activePromoCode: promoCode,
      displayPlanLookup: basePlans.planLookup,
      displaySelectablePlans: basePlans.selectablePlans,
      pricingError: 'promoPricingFailed',
      pricingSource: 'baseFallback',
      pricingState: 'ready',
    };
  }

  if (lastSettled) {
    return {
      activePromoCode: promoCode,
      displayPlanLookup: lastSettled.displayPlanLookup,
      displaySelectablePlans: lastSettled.displaySelectablePlans,
      pricingError: undefined,
      pricingSource: 'previousDisplay',
      pricingState: 'transitioning',
    };
  }

  return {
    activePromoCode: promoCode,
    displayPlanLookup: {},
    displaySelectablePlans: [],
    pricingError: currentPlans.isError ? 'promoPricingFailed' : undefined,
    pricingSource: 'baseFallback',
    pricingState: currentPlans.isError ? 'error' : 'initialLoading',
  };
};

export const useMembershipPlans = ({ validatedPromo }: UseMembershipPlansOptions = {}) => {
  const { enabled: shouldUseBackendPlans } = useFeatureIsOn('should_use_backend_plans');
  const { enabled: isHouseholdPlusActive } = useFeatureIsOn('is_household_plus_active');
  const { value: linkedInPromotionEntityId } = useFeatureValue('linked_in_promotion_entity_id');
  const normalizedPromoCode = validatedPromo?.promoCode;

  const includeLinkedInPlans = Boolean(
    linkedInPromotionEntityId && validatedPromo?.promotionId === linkedInPromotionEntityId,
  );

  const platformDeps = {
    isHouseholdPlusActive,
    shouldUseBackendPlans,
    useRetrievePlanTiersQuery,
  };

  const currentMembershipPlans = useMembershipPlansBase(
    {
      includeLinkedInPlans,
      promoCode: normalizedPromoCode,
    },
    platformDeps,
  );

  const baseMembershipPlans = useMembershipPlansBase({ includeLinkedInPlans }, platformDeps);

  const lastSettledDisplayRef = useRef<SettledMembershipPlans | undefined>(undefined);

  const settledDisplayCandidate = getSettledMembershipPlansCandidate({
    baseMembershipPlans,
    currentMembershipPlans,
    normalizedPromoCode,
  });

  useEffect(() => {
    if (settledDisplayCandidate) {
      // eslint-disable-next-line fp/no-mutation -- Ref update captures last-settled display without triggering a re-render.
      lastSettledDisplayRef.current = settledDisplayCandidate;
    }
  });

  const displayMembershipPlans = useMemo<DisplayMembershipPlans>(
    () =>
      resolveDisplayState({
        basePlans: baseMembershipPlans,
        currentPlans: currentMembershipPlans,
        lastSettled: lastSettledDisplayRef.current,
        promoCode: normalizedPromoCode,
      }),
    [
      baseMembershipPlans.hasCurrentPlanData,
      baseMembershipPlans.isError,
      baseMembershipPlans.planLookup,
      baseMembershipPlans.selectablePlans,
      currentMembershipPlans.hasCurrentPlanData,
      currentMembershipPlans.isError,
      currentMembershipPlans.planLookup,
      currentMembershipPlans.selectablePlans,
      normalizedPromoCode,
    ],
  );

  return {
    activePromoCode: displayMembershipPlans.activePromoCode,
    displayPlanLookup: displayMembershipPlans.displayPlanLookup,
    displaySelectablePlans: displayMembershipPlans.displaySelectablePlans,
    isError: displayMembershipPlans.pricingState === 'error',
    isFetching: currentMembershipPlans.isFetching,
    isLoading: displayMembershipPlans.pricingState === 'initialLoading',
    planLookup: displayMembershipPlans.displayPlanLookup,
    pricingError: displayMembershipPlans.pricingError,
    pricingSource: displayMembershipPlans.pricingSource,
    pricingState: displayMembershipPlans.pricingState,
    refetch: currentMembershipPlans.refetch,
  };
};
